import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Wallet, Users, ArrowDownToLine, Calendar, RefreshCw } from "lucide-react";

const METHOD_LABELS = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  razorpay: "Razorpay",
  other: "Other",
};

export default function AdminCashReconcile() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/cash-reconcile?date=${date}`);
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]);   // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    if (!data?.entries?.length) return;
    const rows = [
      ["Time", "Staff", "Method", "Source", "Ref No", "Amount", "Note"],
      ...data.entries.map((e) => [
        e.at || "",
        e.staff_name || "",
        e.method || "",
        e.source || "",
        e.ref_no || "",
        e.amount || 0,
        (e.note || "").replaceAll("\"", "'"),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cash-reconcile-${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-cash-reconcile">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// End of Day</div>
          <div className="font-display text-3xl font-black uppercase mt-1 flex items-center gap-2">
            <Wallet size={26} /> Cash Drawer Reconcile
          </div>
          <p className="text-xs text-white/50 mt-1 max-w-lg">
            Match this against the physical drawer at end-of-day. Includes cash, UPI, card, cheque, bank transfer & Razorpay across bookings, invoices and kit sales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border border-white/15 px-3 py-2">
            <Calendar size={14} className="text-white/50" />
            <input
              type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="reconcile-date"
              className="bg-transparent text-sm outline-none"
            />
          </div>
          <button onClick={load} disabled={loading}
            data-testid="reconcile-refresh"
            className="border border-white/15 px-3 py-2.5 text-xs uppercase font-mono tracking-widest inline-flex items-center gap-1.5 hover:border-brand-blue"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={exportCsv} disabled={!data?.entries?.length}
            data-testid="reconcile-export"
            className="bg-brand-orange text-black px-4 py-2.5 text-xs uppercase font-bold tracking-widest inline-flex items-center gap-1.5 hover:bg-white disabled:opacity-40"
          >
            <ArrowDownToLine size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Grand totals */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="method-totals">
            {["cash", "upi", "card", "cheque", "bank_transfer", "razorpay", "other"].map((m) => (
              <div key={m} className={`p-4 border ${m === "cash" ? "border-brand-orange bg-brand-orange/5" : "border-white/12 bg-[#131A2B]"}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{METHOD_LABELS[m]}</div>
                <div className={`font-display text-xl font-black mt-1 ${m === "cash" ? "brand-orange" : "brand-blue"}`}
                  data-testid={`method-total-${m}`}>
                  {formatINR(data.totals[m] || 0)}
                </div>
              </div>
            ))}
            <div className="p-4 border border-brand-blue bg-brand-blue/10 md:col-span-1">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">Grand Total</div>
              <div className="font-display text-2xl font-black brand-blue mt-1" data-testid="grand-total">{formatINR(data.grand_total)}</div>
            </div>
          </div>

          {/* Per-staff breakdown */}
          <div className="mb-6" data-testid="per-staff-section">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5">
              <Users size={12} /> By staff — hand this to the founder for reconciliation
            </div>
            <div className="border border-white/12 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
                  <tr>
                    <th className="text-left px-4 py-2.5">Staff</th>
                    <th className="text-right px-4 py-2.5">Cash</th>
                    <th className="text-right px-4 py-2.5">UPI</th>
                    <th className="text-right px-4 py-2.5">Card</th>
                    <th className="text-right px-4 py-2.5">Cheque</th>
                    <th className="text-right px-4 py-2.5">Bank</th>
                    <th className="text-right px-4 py-2.5">Razorpay</th>
                    <th className="text-right px-4 py-2.5">Count</th>
                    <th className="text-right px-4 py-2.5">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_staff.length === 0 && (
                    <tr><td colSpan="9" className="text-center py-8 text-white/40">No collections on this date.</td></tr>
                  )}
                  {data.by_staff.map((s) => (
                    <tr key={s.staff_id || s.staff_name} className="border-b border-white/6 hover:bg-white/5" data-testid={`staff-row-${s.staff_id || "unknown"}`}>
                      <td className="px-4 py-3 font-bold">{s.staff_name}</td>
                      <td className="px-4 py-3 text-right font-mono brand-orange">{formatINR(s.cash)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(s.upi)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(s.card)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(s.cheque)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(s.bank_transfer)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(s.razorpay)}</td>
                      <td className="px-4 py-3 text-right font-mono text-white/60">{s.count}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold brand-blue">{formatINR(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Entries log */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Every collection · {data.entries.length} entries</div>
            <div className="border border-white/12 overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50 sticky top-0 bg-[#0B1120]">
                  <tr>
                    <th className="text-left px-4 py-2.5">Time</th>
                    <th className="text-left px-4 py-2.5">Staff</th>
                    <th className="text-left px-4 py-2.5">Method</th>
                    <th className="text-left px-4 py-2.5">Source</th>
                    <th className="text-left px-4 py-2.5">Ref</th>
                    <th className="text-right px-4 py-2.5">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, i) => (
                    <tr key={i} className="border-b border-white/6" data-testid={`entry-${i}`}>
                      <td className="px-4 py-2 font-mono text-[11px] text-white/50">{e.at ? new Date(e.at).toLocaleTimeString() : "—"}</td>
                      <td className="px-4 py-2">{e.staff_name}</td>
                      <td className="px-4 py-2 uppercase text-[10px] font-mono tracking-widest brand-orange">{e.method}</td>
                      <td className="px-4 py-2 text-[11px] font-mono text-white/60">{e.source}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-white/60">{e.ref_no || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold">{formatINR(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
